import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Maximize2, AlertTriangle, ShieldCheck } from "lucide-react";

export const enterFullscreen = () => document.documentElement.requestFullscreen?.({ navigationUI: "hide" }).catch(() => {});
export const exitFullscreen = () => { if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); };
const isFullscreen = () => !!document.fullscreenElement;

// Tracks tab switches, window blur and fullscreen exits during a secure test; reports each to the backend.
export function useSecureTest({ active, enabled, integrityUrl, initialExits = 0, maxExits = 2, onAutoSubmit }) {
  const [away, setAway] = useState(null); // {exits, maxExits} while the warning overlay is shown
  const [exits, setExits] = useState(initialExits);
  const awayRef = useRef(false);
  const suspended = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active && enabled;
  const onAutoRef = useRef(onAutoSubmit); onAutoRef.current = onAutoSubmit;

  const suspend = useCallback((fn) => { suspended.current = true; try { return fn(); } finally { setTimeout(() => { suspended.current = false; }, 600); } }, []);
  const stop = useCallback(() => { activeRef.current = false; }, []);

  useEffect(() => {
    if (!active || !enabled) return;
    const leave = async (event) => {
      if (!activeRef.current || suspended.current || awayRef.current) return;
      awayRef.current = true;
      let r = { exits: exits + 1, max_exits: maxExits, auto_submit: exits + 1 >= maxExits };
      try { r = (await api.post(integrityUrl, { event })).data; } catch { /* keep local estimate */ }
      setExits(r.exits);
      if (r.auto_submit) { activeRef.current = false; setAway(null); onAutoRef.current?.(r); return; }
      setAway({ exits: r.exits, maxExits: r.max_exits });
    };
    const onVis = () => { if (document.visibilityState === "hidden") leave("hidden"); };
    const onBlur = () => { setTimeout(() => { if (!document.hasFocus() && document.visibilityState === "visible") leave("blur"); }, 150); };
    const onFs = () => { if (!isFullscreen()) leave("fullscreen_exit"); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, [active, enabled, integrityUrl, exits, maxExits]);

  const resume = useCallback(() => { awayRef.current = false; setAway(null); enterFullscreen(); }, []);
  return { away, exits, resume, suspend, stop };
}

// Blocks copy/cut/paste/right-click and text selection on everything inside.
export function NoCopy({ children, className = "", testid }) {
  const block = (e) => e.preventDefault();
  return (
    <div data-testid={testid} className={`select-none ${className}`} onCopy={block} onCut={block} onPaste={block} onContextMenu={block} onDragStart={block}>
      {children}
    </div>
  );
}

export function SecureOverlay({ away, onResume }) {
  if (!away) return null;
  const left = Math.max(0, away.maxExits - away.exits);
  return (
    <div className="fixed inset-0 z-[100] bg-[#060B19]/95 backdrop-blur flex items-center justify-center px-4" data-testid="secure-away-overlay">
      <div className="max-w-md w-full text-center hq-glass rounded-3xl p-8 border border-[#E11D48]/50">
        <div className="w-14 h-14 rounded-2xl bg-[#E11D48]/15 text-[#E11D48] flex items-center justify-center mx-auto mb-4"><ShieldAlert className="w-7 h-7" /></div>
        <p className="text-[10px] uppercase tracking-widest text-[#E11D48] font-mono-data">Warning {away.exits} of {away.maxExits - 1}</p>
        <h2 className="font-display text-3xl mt-1">You left your test</h2>
        <p className="text-sm text-slate-300 mt-3" data-testid="secure-away-msg">
          Switching tabs, opening another window, or leaving fullscreen is recorded and shown to your Guide.
          {left === 1 ? " Leave one more time and your test will be submitted automatically." : ` You can leave ${left} more time${left === 1 ? "" : "s"} before your test is submitted automatically.`}
        </p>
        <Button data-testid="secure-resume-btn" onClick={onResume} className="mt-6 w-full h-11 bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]"><Maximize2 className="w-4 h-4 mr-2" /> Return to my test</Button>
      </div>
    </div>
  );
}

export function SecureRules({ maxExits = 2, compact = false }) {
  const warnings = Math.max(0, maxExits - 1);
  return (
    <ul className={`space-y-1.5 text-slate-200 ${compact ? "text-xs" : "text-sm"}`} data-testid="secure-rules">
      <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#22D3EE] shrink-0 mt-0.5" /> Secure Test Mode: the test runs in fullscreen with the site menu hidden.</li>
      <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-[#FB923C] shrink-0 mt-0.5" /> Switching tabs, opening another window or app, or leaving fullscreen counts as leaving. {warnings === 0 ? "Leaving once submits your test automatically." : `You get ${warnings} warning${warnings === 1 ? "" : "s"}; the next time you leave, your test is submitted automatically.`}</li>
      <li className="flex items-start gap-2"><ShieldAlert className="w-4 h-4 text-[#E11D48] shrink-0 mt-0.5" /> Copy, paste and right-click are turned off. Every exit is shown to your Guide.</li>
    </ul>
  );
}
